/**
 * GraphQL documents for the config-definition sync, written against the aiWARE
 * core schema (services/api/core-graphql-server/schema/schema.graphql).
 *
 * No `orgId` appears in any of them, which is an acceptance criterion: this
 * page manages the environment's definitions, not one organization's values.
 * The definitions read has no orgId argument at all; the three mutations accept
 * an optional one and it is deliberately never sent.
 *
 * `RETRIEVE_CONFIG_VALUES` is the exception worth stating out loud, because
 * omitting `orgId` does NOT mean the same thing there. `applicationConfig`
 * resolves an absent `orgId` to the caller's organization, so that query reads
 * exactly one org's values — the signed-in one — and the page says so. (A
 * superadmin token could pass another org's id and read theirs; this page never
 * does, and has no org selector to do it with.)
 */

/**
 * `limit` defaults to 30 server-side and the manifest alone expects 26
 * definitions, so a single unpaged call sits right on the cliff edge. The
 * service pages regardless of what limit it asks for.
 */
export const RETRIEVE_CONFIG_DEFINITIONS = `
  query redactConfigDefinitions($appId: ID!, $offset: Int, $limit: Int) {
    applicationConfigDefinition(appId: $appId, offset: $offset, limit: $limit) {
      count
      offset
      limit
      records {
        id
        configKey
        configType
        configLevel
        required
        secured
        description
        defaultValue
        defaultValueJSON
      }
    }
  }
`;

/**
 * This organization's own config VALUES, which are a different type behind a
 * different query from the definitions above.
 *
 * `value`/`valueJSON` do not exist on `ApplicationConfigDefinition` — they are
 * on `ApplicationConfig` — so the page cannot tell whether an org has
 * overridden a default without this second read.
 *
 * `includeDefaults: false` is load-bearing, not a micro-optimisation. The
 * default is `true`, which fills `value` in from the definition's default for
 * every key; the page would then flag the entire environment as "custom value"
 * while nobody had set anything. Asking for stored records only means a record
 * existing is already evidence somebody wrote one.
 *
 * No `orgId`, like everything else here. Without one the API answers for the
 * signed-in user's organization — which on this page is the instance root org.
 * So "has a custom value" means THIS org, which is what the detail view says;
 * the page has no org selector and cannot speak for any other tenant.
 */
export const RETRIEVE_CONFIG_VALUES = `
  query redactConfigValues($appId: ID!, $offset: Int, $limit: Int) {
    applicationConfig(
      appId: $appId
      offset: $offset
      limit: $limit
      includeDefaults: false
    ) {
      count
      offset
      limit
      records {
        configKey
        value
        valueJSON
      }
    }
  }
`;

export interface RetrieveConfigValuesResponse {
  readonly applicationConfig: {
    readonly count?: number | null;
    readonly offset: number;
    readonly limit: number;
    readonly records: readonly {
      readonly configKey: string;
      readonly value?: string | null;
      readonly valueJSON?: unknown;
    }[];
  };
}

export interface RetrieveConfigDefinitionsResponse {
  readonly applicationConfigDefinition: {
    readonly count?: number | null;
    readonly offset: number;
    readonly limit: number;
    readonly records: readonly {
      readonly id?: string | null;
      readonly configKey: string;
      readonly configType?: string | null;
      readonly configLevel?: string | null;
      readonly required?: boolean | null;
      readonly secured?: boolean | null;
      readonly description?: string | null;
      readonly defaultValue?: string | null;
      readonly defaultValueJSON?: unknown;
    }[];
  };
}

/** Registers definitions. The whole input array goes in one call. */
export const CREATE_CONFIG_DEFINITIONS = `
  mutation redactCreateConfigDefinitions($input: [ApplicationConfigDefinitionCreate]) {
    applicationConfigDefinitionCreate(input: $input) {
      records {
        id
        configKey
      }
    }
  }
`;

export interface CreateConfigDefinitionsResponse {
  readonly applicationConfigDefinitionCreate: {
    readonly records: readonly { readonly configKey: string }[];
  } | null;
}

/**
 * Amends an existing definition. Used for drift rather than re-running create:
 * `update` is a genuine partial update selected by `filter.configKey`, so it
 * says exactly what it means, whereas create's re-run semantics are documented
 * prose rather than anything the schema guarantees.
 */
export const UPDATE_CONFIG_DEFINITION = `
  mutation redactUpdateConfigDefinition($input: [ApplicationConfigDefinitionUpdateInput]) {
    applicationConfigDefinitionUpdate(input: $input) {
      records {
        id
        configKey
      }
    }
  }
`;

export interface UpdateConfigDefinitionResponse {
  readonly applicationConfigDefinitionUpdate: {
    readonly records: readonly { readonly configKey: string }[];
  } | null;
}

/**
 * Removes a definition from the whole environment.
 *
 * Returns an OperationResult, so a refusal arrives as `success: false` with a
 * message rather than as a thrown GraphQL error. Callers must read `success`;
 * a try/catch alone would report a refused delete as having worked.
 */
export const DELETE_CONFIG_DEFINITION = `
  mutation redactDeleteConfigDefinition($input: ApplicationConfigDefinitionDelete) {
    applicationConfigDefinitionDelete(input: $input) {
      success
      code
      msg
    }
  }
`;

export interface DeleteConfigDefinitionResponse {
  readonly applicationConfigDefinitionDelete: {
    readonly success: boolean;
    readonly code?: string | null;
    readonly msg?: string | null;
  } | null;
}

/**
 * Asks whether this session is in the instance root organization.
 *
 * Its own query, run from the admin page, rather than an addition to the
 * boot-time user/organization fetch: a cluster that does not expose
 * `isRootOrganization` would otherwise fail boot for every user of the app,
 * to serve one page most of them never open.
 */
export const RETRIEVE_ROOT_ORGANIZATION = `
  query redactAdminRootOrganization {
    me {
      organization {
        id
        name
        isRootOrganization
      }
    }
  }
`;

export interface RetrieveRootOrganizationResponse {
  readonly me: {
    readonly organization?: {
      readonly id: string;
      readonly name?: string | null;
      readonly isRootOrganization?: boolean | null;
    } | null;
  } | null;
}
